const getPlaceSearchResults = () => {
  const placeSearchResults = document.getElementById("place-search-results");
  if (!(placeSearchResults instanceof HTMLDivElement)) {
    return [];
  }

  return Array.from(placeSearchResults.querySelectorAll<HTMLDivElement>(".placeSearchResult"));
};

const clearActivePlaceSearchResult = (placeSearchInput: HTMLInputElement) => {
  const placeSearchResults = getPlaceSearchResults();
  for (const result of placeSearchResults) {
    result.setAttribute("data-active", "false");
    result.setAttribute("aria-selected", "false");
  }

  placeSearchInput.dataset.activeIndex = "-1";
  placeSearchInput.removeAttribute("aria-activedescendant");
};

const setActivePlaceSearchResult = (placeSearchInput: HTMLInputElement, index: number) => {
  const placeSearchResults = getPlaceSearchResults();
  if (placeSearchResults.length === 0) {
    clearActivePlaceSearchResult(placeSearchInput);
    return;
  }

  const activeIndex = ((index % placeSearchResults.length) + placeSearchResults.length) % placeSearchResults.length;
  for (const [resultIndex, result] of placeSearchResults.entries()) {
    const isActive = resultIndex === activeIndex;
    result.setAttribute("data-active", isActive ? "true" : "false");
    result.setAttribute("aria-selected", isActive ? "true" : "false");
  }

  const activeResult = placeSearchResults[activeIndex];
  placeSearchInput.dataset.activeIndex = String(activeIndex);
  placeSearchInput.setAttribute("aria-activedescendant", activeResult.id);
  activeResult.scrollIntoView({block: "nearest"});
};

const closePlaceSearchPopover = (target: HTMLElement) => {
  const popoverContent = target.closest("[data-popover-content]");
  const popover = popoverContent?.parentElement;
  if (popover instanceof HTMLElement) {
    popover.dispatchEvent(new CustomEvent("popover:hide"));
  }
};

const initPlaceSearchInput = () => {
  const placeSearchLabel = document.getElementById("place-search-label") as HTMLButtonElement;
  const placeSearchInput = document.getElementById("place-search-input") as HTMLInputElement;
  if (!(placeSearchLabel instanceof HTMLButtonElement) || !(placeSearchInput instanceof HTMLInputElement)) {
    return;
  }
  if (placeSearchInput.getAttribute("data-place-search-input-initialized") === "true") {
    return;
  }

  placeSearchInput.setAttribute("data-place-search-input-initialized", "true");

  placeSearchLabel.addEventListener("click", (e) => {
    e.preventDefault();
    setTimeout(() => {
      placeSearchInput.focus();
    }, 10);
  });

  placeSearchInput.addEventListener("input", () => {
    clearActivePlaceSearchResult(placeSearchInput);
  });

  placeSearchInput.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();

      const placeSearchResults = getPlaceSearchResults();
      if (placeSearchResults.length === 0) {
        return;
      }

      const currentActiveIndex = Number.parseInt(placeSearchInput.dataset.activeIndex || "-1", 10);
      const nextIndex =
        e.key === "ArrowDown"
          ? currentActiveIndex < 0
            ? 0
            : currentActiveIndex + 1
          : currentActiveIndex < 0
            ? placeSearchResults.length - 1
            : currentActiveIndex - 1;

      setActivePlaceSearchResult(placeSearchInput, nextIndex);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      const currentActiveIndex = Number.parseInt(placeSearchInput.dataset.activeIndex || "-1", 10);
      const placeSearchResults = getPlaceSearchResults();
      const activeResult = placeSearchResults[currentActiveIndex];
      if (activeResult) {
        activeResult.click();
      }
    }
  });
};

const initPlaceSearchResults = () => {
  const form = document.getElementById("search-filters") as HTMLFormElement;
  const placeId = document.getElementById("place-id") as HTMLInputElement;
  const placeSearchLabel = document.getElementById("place-search-label") as HTMLButtonElement;
  const placeSearchInput = document.getElementById("place-search-input") as HTMLInputElement;
  const placeSearchResults = document.getElementById("place-search-results") as HTMLDivElement;
  const bounds0 = document.getElementById("bounds-0") as HTMLInputElement;
  const bounds1 = document.getElementById("bounds-1") as HTMLInputElement;
  const bounds2 = document.getElementById("bounds-2") as HTMLInputElement;
  const bounds3 = document.getElementById("bounds-3") as HTMLInputElement;
  if (
    !(form instanceof HTMLFormElement) ||
    !(placeId instanceof HTMLInputElement) ||
    !(placeSearchLabel instanceof HTMLButtonElement) ||
    !(placeSearchInput instanceof HTMLInputElement) ||
    !(placeSearchResults instanceof HTMLDivElement) ||
    !(bounds0 instanceof HTMLInputElement) ||
    !(bounds1 instanceof HTMLInputElement) ||
    !(bounds2 instanceof HTMLInputElement) ||
    !(bounds3 instanceof HTMLInputElement)
  ) {
    return;
  }
  if (placeSearchResults.getAttribute("data-place-search-results-initialized") === "true") {
    return;
  }

  placeSearchResults.setAttribute("data-place-search-results-initialized", "true");

  // Click listener is on the parent instead of each row,
  // because results are swapped in with idiomorph.
  // Some rows might only be modified, not replaced, so adding a listener to each row
  // would cause rows to have multiple listeners.
  placeSearchResults.addEventListener("click", (e) => {
    const clicked = e.target as HTMLElement;
    const target = clicked.closest<HTMLElement>(".placeSearchResult");
    if (target) {
      const data = target.getAttribute("data-place-id");
      if (data) {
        placeId.value = data;

        // When selecting a new place to search, clear the map search custom bounds.
        bounds0.value = "";
        bounds1.value = "";
        bounds2.value = "";
        bounds3.value = "";

        clearActivePlaceSearchResult(placeSearchInput);
        form.dispatchEvent(new Event("change", {bubbles: true}));
        closePlaceSearchPopover(target);
        placeSearchLabel.focus();
      }
    }
  });
};

export const initPlaceSearch = () => {
  initPlaceSearchInput();
  initPlaceSearchResults();
};

const el = document.currentScript;
if (el) {
  initPlaceSearch();
}
